import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { KeyRound, Loader2, ShieldCheck, UserCircle2, CreditCard, ArrowDownCircle, XCircle, AlertTriangle, ArrowUpCircle } from "lucide-react";
import { toast } from "sonner";

const SUB_STATUS = {
  premium: { label: "Assinatura ativa", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  grace: { label: "Carência — pagamento pendente", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  overdue: { label: "Vencida — rodízio pausado", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
  cancelled: { label: "Cancelada", cls: "bg-slate-800 text-slate-400 border-slate-700" },
};

const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("pt-BR");
};

export default function MinhaConta() {
  const { user, refresh } = useAuth();
  const [current, setCurrent] = useState("");
  const [nova, setNova] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [plans, setPlans] = useState([]);
  const [targetPlan, setTargetPlan] = useState("");
  const [billingLoading, setBillingLoading] = useState(false);

  useEffect(() => {
    api.get("/payments/plans").then(({ data }) => setPlans(data)).catch(() => {});
  }, []);

  const subStatus = SUB_STATUS[user?.subscription_status] || { label: "Assinatura inativa", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" };
  const currentPlan = plans.find((p) => p.key === user?.plan);
  const downgradeOptions = currentPlan ? plans.filter((p) => p.price < currentPlan.price) : [];
  const isActive = ["premium", "grace"].includes(user?.subscription_status);
  const graceDate = fmtDate(user?.grace_until);
  const periodEnd = fmtDate(user?.current_period_end);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (nova !== confirm) {
      setError("A confirmação não confere com a nova senha");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: nova });
      toast.success("Senha alterada com sucesso!");
      setCurrent("");
      setNova("");
      setConfirm("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleDowngrade = async () => {
    if (!targetPlan) return;
    const plan = plans.find((p) => p.key === targetPlan);
    if (!window.confirm(`Mudar para o ${plan?.label}? O limite de chips é ajustado na hora.`)) return;
    setBillingLoading(true);
    try {
      const { data } = await api.post("/billing/change-plan", { plan: targetPlan });
      toast.success(`Plano alterado para ${data.plan_label}. Novo limite: ${data.chip_limit} chip(s).`);
      setTargetPlan("");
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBillingLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Cancelar a assinatura? O rodízio dos seus chips pausa imediatamente e você pode reativar quando quiser em Planos.")) return;
    setBillingLoading(true);
    try {
      await api.post("/billing/cancel");
      toast.success("Assinatura cancelada. Você pode reativar a qualquer momento.");
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBillingLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="conta-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">Minha Conta</h1>
        <p className="text-sm text-slate-400 mt-1">Seus dados cadastrais, assinatura e segurança de acesso.</p>
      </div>

      <div className="bg-[#161F32] border border-slate-800 rounded-xl p-5 sm:p-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
            <UserCircle2 className="w-6 h-6 text-emerald-400" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-slate-100 truncate" data-testid="conta-user-name">{user?.name}</p>
            <p className="text-sm text-slate-500 truncate" data-testid="conta-user-email">{user?.email}</p>
            {user?.telefone && (
              <p className="text-xs text-emerald-400 font-mono mt-0.5 truncate" data-testid="conta-user-phone">
                WhatsApp: {user.telefone.length === 11 ? `(${user.telefone.slice(0, 2)}) ${user.telefone.slice(2, 7)}-${user.telefone.slice(7)}` : user.telefone}
              </p>
            )}
          </div>
          <div className="ml-auto flex flex-col items-end gap-1.5">
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-800 text-slate-300 border border-slate-700" data-testid="conta-plan-badge">
              {currentPlan?.label || "Sem plano ativo"}
            </span>
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider border ${subStatus.cls}`} data-testid="conta-subscription-badge">
              {subStatus.label}
            </span>
          </div>
        </div>
      </div>

      {user?.role !== "admin" && (
      <div className="bg-[#161F32] border border-slate-800 rounded-xl p-5 sm:p-6" data-testid="conta-subscription-card">
        <div className="flex items-center gap-2.5 mb-5">
          <CreditCard className="w-5 h-5 text-emerald-400" />
          <h2 className="font-heading text-lg font-semibold text-slate-100">Assinatura</h2>
        </div>

        {user?.subscription_status === "grace" && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-sm text-amber-200 mb-5 flex gap-2.5" data-testid="conta-grace-warning">
            <AlertTriangle className="w-5 h-5 shrink-0 text-amber-400" />
            <span>
              Pagamento pendente. Você está no prazo de carência{graceDate ? ` até ${graceDate}` : ""}.
              Regularize em <Link to="/planos" className="underline font-semibold">Planos</Link> para não pausar o rodízio.
            </span>
          </div>
        )}
        {user?.subscription_status === "overdue" && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-sm text-red-200 mb-5 flex gap-2.5" data-testid="conta-overdue-warning">
            <AlertTriangle className="w-5 h-5 shrink-0 text-red-400" />
            <span>
              Assinatura vencida e rodízio pausado. Pague em <Link to="/planos" className="underline font-semibold">Planos</Link> para retomar automaticamente.
            </span>
          </div>
        )}

        {isActive && currentPlan ? (
          <div className="space-y-5">
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Plano atual</p>
                <p className="text-slate-100 font-semibold" data-testid="conta-current-plan">{currentPlan.label} — R$ {currentPlan.price.toFixed(2).replace(".", ",")}/mês</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Limite de chips</p>
                <p className="text-slate-100 font-semibold font-mono" data-testid="conta-chip-limit">{currentPlan.chip_limit}</p>
              </div>
              {periodEnd && (
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Renovação</p>
                  <p className="text-slate-100 font-semibold font-mono" data-testid="conta-period-end">{periodEnd}</p>
                </div>
              )}
            </div>

            {downgradeOptions.length > 0 && (
              <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                <div className="flex-1 space-y-1.5">
                  <Label htmlFor="downgrade" className="text-slate-300">Mudar para um plano menor</Label>
                  <select
                    id="downgrade"
                    value={targetPlan}
                    onChange={(e) => setTargetPlan(e.target.value)}
                    data-testid="conta-downgrade-select"
                    className="w-full h-10 rounded-md bg-slate-800/60 border border-slate-700 text-slate-100 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">Selecione o novo plano...</option>
                    {downgradeOptions.map((p) => (
                      <option key={p.key} value={p.key}>
                        {p.label} — {p.chip_limit} chip(s) — R$ {p.price.toFixed(2).replace(".", ",")}/mês
                      </option>
                    ))}
                  </select>
                </div>
                <Button onClick={handleDowngrade} disabled={!targetPlan || billingLoading} variant="outline"
                  data-testid="conta-downgrade-button"
                  className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10">
                  {billingLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ArrowDownCircle className="w-4 h-4 mr-2" /> Mudar plano</>}
                </Button>
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-4 border-t border-slate-800">
              <Link to="/planos" data-testid="conta-upgrade-link"
                className="inline-flex items-center text-sm text-emerald-300 hover:text-emerald-200 transition-colors">
                <ArrowUpCircle className="w-4 h-4 mr-1.5" /> Quer mais chips? Faça upgrade na página de Planos
              </Link>
              <Button onClick={handleCancel} disabled={billingLoading} variant="outline"
                data-testid="conta-cancel-button"
                className="border-red-500/40 text-red-300 hover:bg-red-500/10">
                <XCircle className="w-4 h-4 mr-2" /> Cancelar assinatura
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-sm text-slate-400">
            Nenhuma assinatura ativa no momento.{" "}
            <Link to="/planos" className="text-emerald-300 underline font-semibold" data-testid="conta-subscribe-link">
              Escolher um plano
            </Link>
          </div>
        )}
      </div>
      )}

      <div className="bg-[#161F32] border border-slate-800 rounded-xl p-5 sm:p-6">
        <div className="flex items-center gap-2.5 mb-5">
          <KeyRound className="w-5 h-5 text-emerald-400" />
          <h2 className="font-heading text-lg font-semibold text-slate-100">Alterar senha</h2>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-300" data-testid="conta-password-error">
              {error}
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="current" className="text-slate-300">Senha atual</Label>
            <Input id="current" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)}
              placeholder="Sua senha atual" data-testid="conta-current-password-input"
              className="bg-slate-800/60 border-slate-700 text-slate-100 placeholder:text-slate-500 focus-visible:ring-2 focus-visible:ring-emerald-500" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="nova" className="text-slate-300">Nova senha</Label>
            <Input id="nova" type="password" required minLength={6} value={nova} onChange={(e) => setNova(e.target.value)}
              placeholder="Mínimo 6 caracteres" data-testid="conta-new-password-input"
              className="bg-slate-800/60 border-slate-700 text-slate-100 placeholder:text-slate-500 focus-visible:ring-2 focus-visible:ring-emerald-500" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm" className="text-slate-300">Confirmar nova senha</Label>
            <Input id="confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
              placeholder="Repita a nova senha" data-testid="conta-confirm-password-input"
              className="bg-slate-800/60 border-slate-700 text-slate-100 placeholder:text-slate-500 focus-visible:ring-2 focus-visible:ring-emerald-500" />
          </div>
          <Button type="submit" disabled={loading} data-testid="conta-submit-button"
            className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold transition-colors duration-150">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar nova senha"}
          </Button>
          <p className="text-xs text-slate-500 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
            Sua senha é armazenada com criptografia bcrypt e nunca é exibida.
          </p>
        </form>
      </div>
    </div>
  );
}
